import { useState } from "react";
import { RefreshCw, Loader2, AlertTriangle, Link2, Unlink, GitBranch, EyeOff, Copy, Repeat, History } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";

function Stat({ label, value, tone }) {
  const color = tone === "warn" ? "text-yellow-400" : tone === "err" ? "text-red-400" : tone === "ok" ? "text-green-400" : "text-white";
  return <div className="rounded-lg border border-white/10 bg-white/5 p-3"><p className={`text-2xl font-bold ${color}`}>{value}</p><p className="text-[11px] text-white/50">{label}</p></div>;
}

function Group({ title, icon: Icon, items, render, empty }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid={`link-group-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <p className="text-sm font-medium mb-2 flex items-center gap-2"><Icon className="h-4 w-4 text-gold" /> {title} ({items?.length || 0})</p>
      {(!items || items.length === 0) ? <p className="text-xs text-white/40">{empty}</p> :
        <ul className="space-y-1 max-h-52 overflow-y-auto">{items.map((it, i) => <li key={i} className="text-xs text-white/70">{render(it)}</li>)}</ul>}
    </div>
  );
}

export default function LinkHealthPanel() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/seo/link-health").then(({ data }) => setHealth(data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/admin/seo/link-audit?limit=50").then(({ data }) => setAudit(data.logs || [])).catch(() => {});
  };

  return (
    <div className="space-y-5" data-testid="link-health-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-white/60">Internal link health across all products, categories, brands and pages. Orphan pages have no incoming internal links.</p>
        <Button variant="outline" className="gap-2" onClick={load} disabled={loading} data-testid="link-health-refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {health ? "Rescan" : "Run scan"}
        </Button>
      </div>
      {!health && !loading && <p className="text-white/40 text-sm">Click "Run scan" to analyse internal linking.</p>}
      {health && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Total Links" value={health.summary.total_links} />
            <Stat label="Orphan Pages" value={health.summary.orphans} tone={health.summary.orphans ? "warn" : "ok"} />
            <Stat label="Broken Links" value={health.summary.broken} tone={health.summary.broken ? "err" : "ok"} />
            <Stat label="Redirect Links" value={health.summary.redirect_links} tone={health.summary.redirect_links ? "warn" : "ok"} />
            <Stat label="No Outgoing" value={health.summary.no_outgoing} tone={health.summary.no_outgoing ? "warn" : "ok"} />
            <Stat label="Unpublished Targets" value={health.summary.unpublished_targets} tone={health.summary.unpublished_targets ? "warn" : "ok"} />
            <Stat label="Duplicate Links" value={health.summary.duplicates} tone={health.summary.duplicates ? "warn" : "ok"} />
            <Stat label="Repeated Anchors" value={health.summary.excessive_anchor} tone={health.summary.excessive_anchor ? "warn" : "ok"} />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Group title="Orphan Pages" icon={Unlink} items={health.orphans} empty="No orphan pages — every page has an incoming link." render={(o) => <span><b className="text-white/80">{o.name}</b> <span className="text-white/40">({o.type})</span> — add an internal link pointing here.</span>} />
            <Group title="Pages With No Outgoing Links" icon={Link2} items={health.no_outgoing} empty="Every page links out to related content." render={(o) => <span><b className="text-white/80">{o.name}</b> <span className="text-white/40">({o.type})</span></span>} />
            <Group title="Broken Links" icon={AlertTriangle} items={health.broken} empty="No broken links." render={(b) => <span>{b.source} → missing {b.target_type} ({b.target_id})</span>} />
            <Group title="Links To Unpublished" icon={EyeOff} items={health.unpublished_targets} empty="No links to unpublished content." render={(u) => <span>{u.source} → {u.target}</span>} />
            <Group title="Redirect Links" icon={GitBranch} items={health.redirect_links} empty="No links pointing at redirects." render={(r) => <span>{r.source} → {r.target}</span>} />
            <Group title="Duplicate Links" icon={Copy} items={health.duplicates} empty="No duplicate links on any page." render={(d) => <span>{d.source} links to {d.target} more than once</span>} />
            <Group title="Excessive Repeated Anchor Text" icon={Repeat} items={health.excessive_anchor} empty="Anchor text is well varied." render={(a) => <span>"{a.anchor}" used {a.count}× — vary it to avoid over-optimisation.</span>} />
            <Group title="High Link Count" icon={Link2} items={health.high_link} empty="No pages with unusually many links." render={(h) => <span>{h.name} — {h.count} outgoing links</span>} />
          </div>
        </>
      )}
      {audit && audit.length > 0 && (
        <div className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid="link-audit">
          <p className="text-sm font-medium mb-2 flex items-center gap-2"><History className="h-4 w-4 text-gold" /> Recent Linking Activity</p>
          <ul className="space-y-1 max-h-56 overflow-y-auto">
            {audit.map((l) => (
              <li key={l.audit_id} className="text-xs text-white/60 flex items-center gap-2">
                <span className="text-white/40 whitespace-nowrap">{new Date(l.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] ${l.action === "accept" ? "bg-green-500/20 text-green-300" : l.action === "auto_update_slug" ? "bg-sky-500/15 text-sky-300" : "bg-white/10 text-white/50"}`}>{l.action}</span>
                <span className="truncate">{l.source_type}:{l.source_id} → {l.target_type}:{l.target_id} <span className="text-white/35">· {l.actor}</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
